import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  listTransactions,
  type TransactionFilters,
  type TransactionSummary,
} from '@/api/transactions'

// Extra pages loaded via "Load more" — keyed by the filter snapshot so they drop away when the
// filters change, without a separate reset effect.
type MoreState = {
  filterKey: string
  transactions: TransactionSummary[]
  nextCursor: string | null
}

/** The first page through React Query (so mutations' invalidation refreshes it) plus Load more. */
export function useTransactionPages(filters: TransactionFilters, opts: { enabled?: boolean } = {}) {
  const [more, setMore] = useState<MoreState>({ filterKey: '', transactions: [], nextCursor: null })
  const filterKey = JSON.stringify(filters)

  const query = useQuery({
    queryKey: ['transactions', filters],
    queryFn: () => listTransactions(filters),
    enabled: opts.enabled ?? true,
  })

  const sameFilters = more.filterKey === filterKey
  const cursor = sameFilters ? more.nextCursor : (query.data?.nextCursor ?? null)

  const loadMore = useMutation({
    mutationFn: () => listTransactions({ ...filters, cursor: cursor ?? undefined }),
    onSuccess: (d) => {
      setMore((prev) => ({
        filterKey,
        transactions: [
          ...(prev.filterKey === filterKey ? prev.transactions : []),
          ...d.transactions,
        ],
        nextCursor: d.nextCursor,
      }))
    },
  })

  const transactions = useMemo(
    () => [...(query.data?.transactions ?? []), ...(sameFilters ? more.transactions : [])],
    [query.data, sameFilters, more.transactions]
  )

  return {
    data: query.data,
    transactions,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore: !!cursor,
    loadMore: () => loadMore.mutate(),
    isLoadingMore: loadMore.isPending,
  }
}
